"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"

import { cn } from "@/lib/utils"
import { CheckIcon } from "@/src/shared/assets/icons"

const checkboxVariants = cva(
  "peer size-[20px] shrink-0 cursor-pointer overflow-hidden rounded-[6px] ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border-[1px] border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
        brand:
          "relative bg-black-800 text-white after:pointer-events-none after:absolute after:inset-0 after:z-20 after:rounded-[6px] after:p-px after:content-[''] after:bg-linear-to-b after:from-white/15 after:to-black/15 after:[mask-image:linear-gradient(--theme(--color-black),--theme(--color-black)),linear-gradient(--theme(--color-black),--theme(--color-black))] after:[mask-clip:content-box,border-box] after:[mask-composite:exclude]",
      },
      size: {
        default: "",
        sm: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

const checkboxIndicatorVariants = cva("", {
  variants: {
    variant: {
      default: "flex items-center justify-center text-current",
      brand:
        "absolute inset-0 rounded-[6px] bg-linear-to-t from-brand-fill to-brand-ring p-0.5 before:block before:size-full before:rounded-[4px] before:bg-linear-to-t before:from-brand-fill before:from-15% before:to-brand-ring before:content-['']",
    },
    size: {
      default: "",
      sm: "",
    },
  },
  compoundVariants: [
    {
      variant: "brand",
      size: "sm",
      class: "p-px",
    },
  ],
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

const checkboxIconVariants = cva("pointer-events-none h-[9px] w-[12px]", {
  variants: {
    variant: {
      default: "",
      brand: "absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2",
    },
    size: {
      default: "",
      sm: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
})

export type CheckboxProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> &
  VariantProps<typeof checkboxVariants>

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant, size, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ variant, size }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn(checkboxIndicatorVariants({ variant, size }))}
    >
      <CheckIcon
        width={12}
        height={9}
        className={cn(checkboxIconVariants({ variant, size }))}
        aria-hidden
      />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox, checkboxVariants }
